import {
  getChatNotificationSoundOption,
  readChatNotificationSoundId,
  readChatNotificationSoundMuted,
  chatNotificationSoundUrl,
  type ChatNotificationSoundId,
} from "@/lib/chatNotificationSoundPrefs";
import { chatSendPopSoundUrl } from "@/lib/chatSendPopPrefs";

let lastPlayAt = 0;
const MIN_GAP_MS = 900;
const audioById = new Map<ChatNotificationSoundId, HTMLAudioElement>();

let lastSendPopAt = 0;
let sendPopEl: HTMLAudioElement | null = null;

const CHAT_LONGPRESS_SOUND_FILE = "Cartoon Multi Pops.wav";
const CHAT_AURIZ_SOUND_FILE = "Pop Ding Notification 05.wav";
const CHAT_EMOJI_SOUND_FILE = "Accept Popper02.mp3";
const CHAT_IMPACT_SOUND_FILE = "Weird Whoosh Impact 05.wav";
const CHAT_ECHO_SOUND_FILE = "Echo Pop Pulse.wav";
const CHAT_VOICE_OPEN_SOUND_FILE = "Cartoon Pop Open.wav";
const CHAT_DELETE_SOUND_FILE = "Pop Paper.wav";

const uiSoundEls = new Map<string, HTMLAudioElement>();
const lastLongPress = { t: 0 };
const lastAuriz = { t: 0 };
const lastEmoji = { t: 0 };
const lastImpact = { t: 0 };
const lastEcho = { t: 0 };
const lastVoiceOpen = { t: 0 };
const lastDeletePop = { t: 0 };

function ensureUiSound(file: string, volume: number): HTMLAudioElement {
  let el = uiSoundEls.get(file);
  if (!el) {
    el = new Audio(chatNotificationSoundUrl(file));
    el.preload = "auto";
    el.volume = volume;
    uiSoundEls.set(file, el);
  }
  return el;
}

function playUiSound(file: string, volume: number, lastAt: { t: number }, gapMs: number): void {
  if (readChatNotificationSoundMuted()) return;
  const now = Date.now();
  if (now - lastAt.t < gapMs) return;
  lastAt.t = now;
  try {
    const el = ensureUiSound(file, volume);
    el.currentTime = 0;
    void el.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
}

function preloadUiSound(file: string, volume: number): void {
  try {
    ensureUiSound(file, volume);
  } catch {
    /* ignore */
  }
}

function unlockUiSound(file: string, volume: number): void {
  try {
    const el = ensureUiSound(file, volume);
    el.muted = true;
    void el
      .play()
      .then(() => {
        el.pause();
        el.currentTime = 0;
        el.muted = false;
      })
      .catch(() => {
        el.muted = false;
      });
  } catch {
    /* ignore */
  }
}

function ensureAudio(id: ChatNotificationSoundId): HTMLAudioElement | null {
  const option = getChatNotificationSoundOption(id);
  if (!option.file) return null;
  let el = audioById.get(id);
  if (el) return el;
  el = new Audio(chatNotificationSoundUrl(option.file));
  el.preload = "auto";
  audioById.set(id, el);
  return el;
}

function ensureSendPopAudio(): HTMLAudioElement {
  if (sendPopEl) return sendPopEl;
  sendPopEl = new Audio(chatSendPopSoundUrl());
  sendPopEl.preload = "auto";
  sendPopEl.volume = 0.55;
  return sendPopEl;
}

export function unlockChatNotificationSound(): void {
  const id = readChatNotificationSoundId();
  const el = ensureAudio(id);
  if (el) {
    el.muted = true;
    void el
      .play()
      .then(() => {
        el.pause();
        el.currentTime = 0;
        el.muted = false;
      })
      .catch(() => {
        el.muted = false;
      });
  }
  try {
    const pop = ensureSendPopAudio();
    pop.muted = true;
    void pop
      .play()
      .then(() => {
        pop.pause();
        pop.currentTime = 0;
        pop.muted = false;
      })
      .catch(() => {
        pop.muted = false;
      });
  } catch {
    /* ignore */
  }
  unlockUiSound(CHAT_LONGPRESS_SOUND_FILE, 0.55);
  unlockUiSound(CHAT_AURIZ_SOUND_FILE, 0.6);
  unlockUiSound(CHAT_EMOJI_SOUND_FILE, 0.5);
  unlockUiSound(CHAT_IMPACT_SOUND_FILE, 0.65);
  unlockUiSound(CHAT_ECHO_SOUND_FILE, 0.55);
  unlockUiSound(CHAT_DELETE_SOUND_FILE, 0.55);
  preloadUiSound(CHAT_VOICE_OPEN_SOUND_FILE, 0.6);
}

export function playChatNotificationSound(): void {
  if (readChatNotificationSoundMuted()) return;
  const id = readChatNotificationSoundId();
  if (id === "off") return;
  const now = Date.now();
  if (now - lastPlayAt < MIN_GAP_MS) return;
  lastPlayAt = now;
  const el = ensureAudio(id);
  if (!el) return;
  try {
    el.currentTime = 0;
    void el.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
}

/** À appeler au pointerdown (geste encore valide) pour iOS. */
export function unlockChatLongPressPop(): void {
  unlockUiSound(CHAT_LONGPRESS_SOUND_FILE, 0.55);
}

/** Appui long / clic droit sur un message — Cartoon Multi Pops. */
export function playChatLongPressPop(): void {
  playUiSound(CHAT_LONGPRESS_SOUND_FILE, 0.55, lastLongPress, 180);
}

/** Auriz — Pop Ding Notification 05. */
export function playChatAuriz(): void {
  playUiSound(CHAT_AURIZ_SOUND_FILE, 0.6, lastAuriz, 120);
}

/** Emoji (réaction ou insertion) — Accept Popper02. */
export function playChatEmojiPop(): void {
  playUiSound(CHAT_EMOJI_SOUND_FILE, 0.5, lastEmoji, 70);
}

/** Effet Impact — Weird Whoosh Impact 05. */
export function playChatImpact(): void {
  playUiSound(CHAT_IMPACT_SOUND_FILE, 0.65, lastImpact, 200);
}

/** Effet Écho — Echo Pop Pulse. */
export function playChatEcho(): void {
  playUiSound(CHAT_ECHO_SOUND_FILE, 0.55, lastEcho, 180);
}

function playClonedUiSound(file: string, volume: number, lastAt: { t: number }): void {
  if (readChatNotificationSoundMuted()) return;
  const now = Date.now();
  if (now - lastAt.t < 80) return;
  lastAt.t = now;
  try {
    const src = ensureUiSound(file, volume);
    const el = src.cloneNode(true) as HTMLAudioElement;
    el.muted = false;
    el.volume = volume;
    void el.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
}

let voicePopKeepAlive: HTMLAudioElement | null = null;

/** Joue un pop vocal. `holdMs` : on attend au plus ça avant de rendre la main (l’audio reste tenu jusqu’à ended). */
function playVoicePopThrough(
  file: string,
  volume: number,
  lastAt: { t: number },
  holdMs: number
): Promise<void> {
  if (readChatNotificationSoundMuted()) return Promise.resolve();
  const now = Date.now();
  if (now - lastAt.t < 80) return Promise.resolve();
  lastAt.t = now;
  return new Promise((resolve) => {
    try {
      const el = new Audio(chatNotificationSoundUrl(file));
      el.preload = "auto";
      el.muted = false;
      el.volume = volume;
      voicePopKeepAlive = el;
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const release = () => {
        el.onended = null;
        el.onerror = null;
        if (voicePopKeepAlive === el) voicePopKeepAlive = null;
        settle();
      };
      const cap = window.setTimeout(settle, holdMs);
      el.onended = () => {
        window.clearTimeout(cap);
        release();
      };
      el.onerror = () => {
        window.clearTimeout(cap);
        release();
      };
      void el.play().catch(() => {
        window.clearTimeout(cap);
        release();
      });
    } catch {
      resolve();
    }
  });
}

/** Début d’enregistrement vocal — Cartoon Pop Open. */
export function playChatVoiceOpen(): Promise<void> {
  return playVoicePopThrough(CHAT_VOICE_OPEN_SOUND_FILE, 0.6, lastVoiceOpen, 900);
}

/** Suppression d’un message — Pop Paper. */
export function playChatDeletePop(): void {
  playClonedUiSound(CHAT_DELETE_SOUND_FILE, 0.6, lastDeletePop);
}

/** Petit son à l’envoi — Sweet UI Open (fixe). */
export function playChatSendPop(): void {
  const now = Date.now();
  if (now - lastSendPopAt < 90) return;
  lastSendPopAt = now;
  try {
    const pop = ensureSendPopAudio();
    pop.currentTime = 0;
    void pop.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
}

export async function previewChatNotificationSound(id: ChatNotificationSoundId): Promise<void> {
  if (id === "off") return;
  const el = ensureAudio(id);
  if (!el) return;
  el.currentTime = 0;
  await el.play().catch(() => undefined);
}
